import type { ReactNode, Ref } from 'react';
import { cn } from '../../lib/utils';
import { ChevronDownIcon } from './ChevronDownIcon';
import { fieldClass } from './fieldClass';

/**
 * - `field` (default): the bordered form-field look every picker uses
 *   (`fieldClass`), with no hover border, like every other form field.
 * - `compact`: a quiet toolbar field for a white header - `bg-bg-subtle` at
 *   rest, white with the primary border on hover, no vertical padding (the
 *   caller sets the height, e.g. `h-9`).
 * - `raised`: the same toolbar field for a header with no surface of its own,
 *   sitting on the grey page background - white at rest so it stands off the
 *   page, primary border on hover.
 */
export type SearchFieldTriggerAppearance = 'field' | 'compact' | 'raised';

const LAYOUT_CLASS = 'flex w-full items-center justify-between gap-2 px-3 text-left';

const TOOLBAR_CLASSES: Record<Exclude<SearchFieldTriggerAppearance, 'field'>, string> = {
  compact: 'rounded-lg border bg-bg-subtle text-sm transition-colors hover:bg-bg-primary',
  raised: 'rounded-lg border bg-bg-primary text-sm transition-colors',
};

function triggerClass(appearance: SearchFieldTriggerAppearance, hasError: boolean): string {
  if (appearance === 'field') return cn(fieldClass({ error: hasError }), LAYOUT_CLASS, 'py-2');
  return cn(
    LAYOUT_CLASS,
    TOOLBAR_CLASSES[appearance],
    'focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20',
    'disabled:cursor-not-allowed disabled:bg-bg-subtle disabled:opacity-50',
    hasError ? 'border-error' : 'border-border hover:border-primary',
  );
}

interface SearchFieldTriggerProps {
  triggerRef: Ref<HTMLButtonElement>;
  open: boolean;
  disabled?: boolean;
  /** Renders the error border and marks the trigger `aria-invalid`. */
  hasError?: boolean;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  /** id of the caller's own visible error text, when it has one to link. */
  ariaDescribedBy?: string;
  /** Rendered inside the truncating content span - the placeholder or the selected value. */
  children: ReactNode;
  /** Extra classes for the content span, e.g. text colour. Defaults to `truncate`. */
  contentClassName?: string;
  /** id on the content span, for a caller that labels the trigger via `aria-labelledby` rather than `aria-label`. */
  contentId?: string;
  /** Replaces the default chevron. Pass `null` to render nothing (e.g. when a separate clear button occupies that space). */
  accessory?: ReactNode;
  /** Extra classes for the button itself, e.g. reserving space for a clear button placed outside it. */
  className?: string;
  /** Visual treatment. Defaults to the bordered form field. */
  appearance?: SearchFieldTriggerAppearance;
  /** Advertised keyboard shortcut, e.g. `Meta+K Control+K`, as `aria-keyshortcuts`. */
  ariaKeyShortcuts?: string;
}

/**
 * The input-styled button that opens a `SearchPopover` over itself: a
 * bordered field matching `Input`'s border/focus treatment, and `invisible` (not unmounted)
 * while open so the popover's overlay anchor keeps its layout slot.
 * Content, the clear affordance, and the popover itself are the caller's -
 * this owns only the trigger surface. Canonical adopters:
 * `bank-statement/BankLineInlinePanel.tsx`, `bank-statement/MoneyInReconcilePanel.tsx`,
 * `bank-statement/MoneyOutReconcileRows.tsx`, `ledger/assets/AcquisitionJournalPicker.tsx`.
 */
export function SearchFieldTrigger({
  triggerRef,
  open,
  disabled,
  hasError,
  onClick,
  ariaLabel,
  ariaLabelledBy,
  ariaDescribedBy,
  children,
  contentClassName,
  contentId,
  accessory,
  className,
  appearance = 'field',
  ariaKeyShortcuts,
}: SearchFieldTriggerProps) {
  return (
    <button
      ref={triggerRef}
      type="button"
      disabled={disabled}
      aria-expanded={open}
      aria-haspopup="listbox"
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      aria-invalid={hasError ? true : undefined}
      aria-keyshortcuts={ariaKeyShortcuts}
      onClick={onClick}
      className={cn(
        triggerClass(appearance, Boolean(hasError)),
        open ? 'invisible' : '',
        className,
      )}
    >
      <span id={contentId} className={contentClassName ?? 'truncate'}>{children}</span>
      {accessory !== undefined ? accessory : <ChevronDownIcon className="h-4 w-4 flex-shrink-0 text-text-light" />}
    </button>
  );
}
